import { z } from "zod";
import { ChallanStatusSchema, ChallanTypeSchema, CopyType, CopyTypeSchema, CreditNoteStatusSchema, PaymentMethodSchema, PricingModeSchema, QuotationStatusSchema, SupplyNatureSchema, TaxDisplayMode, TaxDisplayModeSchema, } from "./enums.js";
import { ClaimableQuotationSendChannelSchema } from "./approval.js";
import { PanSchema } from "./company.js";
import { GstinSchema } from "./gstin.js";
import { BooleanQuerySchema, GstRateBpSchema, IdSchema, IsoDateSchema, LengthMmSchema, PaiseSchema, PhoneSchema, PiecesSchema, PincodeSchema, QtyMilliSchema, WeightGramsSchema, clearable, optionalQuery, optionalText, } from "./primitives.js";
import { StateCodeSchema } from "./state-codes.js";
import { DEFAULT_UNIT_LABEL, UnitLabelSchema } from "./units.js";
/**
 * Request contracts for creating documents.
 *
 * SERVER-AUTHORITY RULE (spec §6): the client sends lines, quantities and rates.
 * It NEVER sends taxable values, tax amounts, round-off, totals, supply type or
 * document numbers — the server computes all of those with `@steelbill/tax` and
 * only its results are persisted. These schemas enforce that structurally: no
 * computed money field exists on them, and because `z.object()` strips unknown
 * keys by default, a client that sends `taxablePaise` or `grandTotalPaise`
 * simply has them dropped before the handler ever sees the payload.
 *
 * Phase 2 extends these. Each refined schema is published alongside its
 * unrefined `...BaseSchema` so `.extend()` / `.merge()` stay available.
 */
const LineKeySchema = z
    .string()
    .trim()
    .min(1, "Line key cannot be empty")
    .max(64, "Line key is too long");
/* -------------------------------------------------------------------------- */
/* Document line                                                               */
/* -------------------------------------------------------------------------- */
export const DocumentLineBaseSchema = z.object({
    /**
     * Client-side stable key for this line. Children point at their parent with
     * `parentKey`, so a document that uses the two-level hierarchy must supply
     * keys. The server assigns `line_code` ("10", "10-10") itself.
     */
    key: LineKeySchema.optional(),
    /** Key of the group header this line sits under (spec §3 parent/child rules). */
    parentKey: LineKeySchema.nullish(),
    /**
     * Grouping header. Carries the job title and contributes NOTHING to the
     * totals; its printed rate is derived by the server as the sum of its
     * children, never typed (spec §3, §8.1 — the invoice-66 defect).
     */
    isGroupHeader: z.boolean().optional(),
    /** Printed SR.NO, e.g. "10" or "10-20". Server-assigned when omitted. */
    lineCode: optionalText(16),
    /** Catalogue item this line came from. Null / absent for ad-hoc lines (§3.1). */
    itemId: IdSchema.nullish(),
    /** Snapshot of the description. Multi-line is expected (§10 voice input). */
    description: z
        .string()
        .trim()
        .min(1, "Description is required")
        .max(1000, "Description is too long"),
    /**
     * HSN (goods) or SAC (service). Legally mandatory on every tax invoice line —
     * the app pre-fills it but must never let a line be saved without it (§3.1).
     *
     * Capped at 8 to match `quotation_lines.hsn` / `invoice_lines.hsn`
     * `@db.VarChar(8)`. A legal HSN is 2, 4, 6 or 8 digits and a SAC is 6, so 8 is
     * the real ceiling — anything longer must be rejected here with a clean
     * validation error rather than reaching MariaDB, which would either raise an
     * opaque ER_DATA_TOO_LONG or (with strict mode relaxed) silently truncate and
     * print a wrong code on a legally-mandated invoice column.
     */
    hsn: z.string().trim().min(1, "HSN/SAC is required").max(8, "HSN/SAC is too long"),
    /** Goods vs works contract (§7.1). Snapshot from the item, overridable per line. */
    supplyNature: SupplyNatureSchema.optional(),
    gstRateBp: GstRateBpSchema,
    pricingMode: PricingModeSchema,
    qtyMilli: QtyMilliSchema,
    unitLabel: UnitLabelSchema.default(DEFAULT_UNIT_LABEL),
    /** Required by BY_PIECE / BY_WEIGHT / BY_LENGTH respectively (§4). */
    pieces: PiecesSchema.nullish(),
    weightGrams: WeightGramsSchema.nullish(),
    lengthMm: LengthMmSchema.nullish(),
    /** Rate per unit in paise. An input, not a computed total. */
    ratePaise: PaiseSchema,
    /** Free-typed one-off line rather than one picked from the rate card (§3.1). */
    isAdhoc: z.boolean().default(false),
});
/**
 * The per-pricing-mode requirements of §4, factored out of the schema so the
 * DISCOUNTABLE variant below applies exactly the same ones.
 *
 * It is a function rather than a second `.superRefine()` chained on the extended
 * schema for one reason: a line's requirements must not depend on whether the
 * document it sits on can carry a discount. Two hand-kept copies of this switch
 * would eventually disagree about, say, whether a `BY_WEIGHT` line needs a
 * weight, and the copy that drifts is the one on the document type nobody
 * happened to test.
 */
function refineLineByPricingMode(line, ctx) {
    if (line.isGroupHeader === true) {
        if (line.parentKey !== null && line.parentKey !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["parentKey"],
                message: "A group header cannot sit under another line — documents are at most two levels deep",
            });
        }
        // A header carries no billable measurement; its rate is derived from its children.
        return;
    }
    switch (line.pricingMode) {
        case "LUMPSUM":
            // taxable = ratePaise × qtyMilli; qtyMilli is already required and positive.
            break;
        case "BY_PIECE":
            if (line.pieces === null || line.pieces === undefined) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["pieces"],
                    message: "pieces is required when the line is priced by piece",
                });
            }
            break;
        case "BY_WEIGHT":
            if (line.weightGrams === null || line.weightGrams === undefined) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["weightGrams"],
                    message: "weightGrams is required when the line is priced by weight",
                });
            }
            break;
        case "BY_LENGTH":
            if (line.lengthMm === null || line.lengthMm === undefined) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["lengthMm"],
                    message: "lengthMm is required when the line is priced by length",
                });
            }
            break;
        default: {
            const exhaustive = line.pricingMode;
            throw new Error(`Unhandled pricing mode: ${String(exhaustive)}`);
        }
    }
}
/**
 * One line of a create request, with the per-pricing-mode requirements from §4
 * applied. Group headers are exempt: they are titles, not billable work.
 *
 * NO DISCOUNT FIELD, and its absence is the mechanism that keeps §15(3)(a)
 * discounts off challans and credit notes — see
 * {@link DiscountableDocumentLineSchema}.
 */
export const DocumentLineSchema = DocumentLineBaseSchema.superRefine(refineLineByPricingMode);
/* -------------------------------------------------------------------------- */
/* Discounts — §15(3)(a) CGST. Invoices and quotations ONLY                     */
/* -------------------------------------------------------------------------- */
/**
 * ═══════════════════════════════════════════════════════════════════════════
 * A DISCOUNT IS A TAX CHANGE, NOT A DISPLAY ONE.
 *
 * §15(3)(a) CGST: a discount given at or before the time of supply AND RECORDED
 * IN THE INVOICE is excluded from the value of supply. So it reduces the TAXABLE
 * VALUE and GST is charged on the reduced figure. A line at the bottom that
 * subtracts from the grand total after tax overstates output tax on every
 * discounted bill.
 *
 * PAISE IS THE RECORD; A PERCENTAGE IS ONLY AN INPUT. "10%" re-evaluated later
 * against an edited line silently resolves to a different bill from the one the
 * customer accepted and the CA filed, and §15(3)(a) requires the DISCOUNT — the
 * money — to be recorded in the invoice. So a percentage sent here is resolved
 * to paise ONCE, by the server, at save (`resolveDiscountPercent`,
 * services/documents.ts), and it is that amount the engine taxes and the columns
 * hold. The percentage is stored beside it as PROVENANCE — a note of how the
 * figure was reached, printed in the `LESS DISCOUNT (10%)` bracket — and is
 * never re-applied on read by anything.
 *
 * WHICH IS WHY EACH DISCOUNT TAKES ONE OR THE OTHER, NEVER BOTH. Two answers to
 * "what is the discount" means the server picking one silently, and the one it
 * picks is the one the owner was not looking at. See {@link DISCOUNT_PERCENT_AND_AMOUNT}.
 *
 * IT IS AN INPUT, NOT A TOTAL (§7). The server subtracts it inside
 * `@steelbill/tax` and recomputes every taxable value and tax figure from the
 * result; nothing here is a number the client has already applied.
 *
 * WHY THESE FIELDS EXIST ON A SEPARATE SCHEMA rather than on
 * `DocumentLineBaseSchema`, which every document type shares: SCOPE IS INVOICES
 * AND QUOTATIONS. A delivery challan declares the value of goods in motion
 * (Rule 55) rather than a negotiated price, and a credit note is already a
 * reduction — discounting one is incoherent. Both go through the SAME
 * `prepareDocumentLines` and the SAME engine, so the exclusion cannot be a
 * branch in the arithmetic; it is enforced structurally, by their request
 * schemas not carrying the field at all. `z.object()` strips unknown keys, so a
 * challan that posts `discountPaise` simply has it dropped before any handler
 * sees it.
 *
 * WHAT IS DELIBERATELY NOT CHECKED HERE: that a discount does not exceed what it
 * discounts. That comparison needs the line's taxable value, which is
 * `qty × rate` by pricing mode — tax-engine arithmetic, and CLAUDE.md keeps ALL
 * of it in `packages/tax`. `computeTotals` refuses an excessive discount per
 * line and for the document, and the API surfaces its sentence as a 400. One
 * implementation, one message.
 * ═══════════════════════════════════════════════════════════════════════════
 */
/** 100%, in basis points. A discount may equal what it comes off, never exceed it. */
export const DISCOUNT_PERCENT_BP_MAX = 10_000;
/**
 * A discount percentage, in BASIS POINTS to match `gstRateBp` — 10% is `1000`,
 * 7.5% is `750`, 100% is `10000`.
 *
 * BASIS POINTS RATHER THAN A FRACTION, for the reason every rate in this system
 * is: `0.1` is not representable in binary floating point, and a percentage that
 * arrives as `0.30000000000000004` resolves against a ₹1,00,000 line to a
 * different paisa than `3000` does. Integers all the way down (CLAUDE.md).
 *
 * `0` IS ACCEPTED AND `10000` IS ACCEPTED. 0% is a discount of nothing, which is
 * a strange thing to type but not a wrong one, and 100% of a line is a
 * legitimate full write-down — the engine's own rule is that a discount may not
 * EXCEED what it discounts, and 100% does not. Above 100% is refused here rather
 * than left to the engine, because the engine sees only the resolved paise and
 * its refusal would name an amount the owner never typed.
 */
export const DiscountPercentBpSchema = z
    .number()
    .int("A discount percentage must be whole basis points — 10% is 1000")
    .min(0, "A discount percentage cannot be negative")
    .max(DISCOUNT_PERCENT_BP_MAX, "A discount cannot be more than 100% — that is more than the thing it comes off");
/**
 * Basis points as the label a human reads: `1000` → `"10%"`, `750` → `"7.5%"`,
 * `1234` → `"12.34%"`, `10000` → `"100%"`.
 *
 * ONE IMPLEMENTATION, SHARED BY THE API'S WARNINGS AND BY THE PRINTED SHEET.
 * `services/documents.ts` had a private copy of this for the §8.7 rate warnings
 * and `services/pdf/blocks.ts` needs the identical string inside the
 * `LESS DISCOUNT (10%)` bracket. Two copies would eventually disagree about
 * `750` — "7.5%" or "7.50%" — and the one that drifts is the one on the sheet
 * that goes to the customer.
 *
 * NO MONEY IS DIVIDED HERE, so CLAUDE.md's `divRoundHalfUp` rule is not in play:
 * this splits an integer rate into its two printed halves and formats digits.
 * Nothing it returns is ever parsed back into arithmetic.
 */
export function formatPercentBp(bp) {
    const whole = Math.trunc(bp / 100);
    const fraction = Math.abs(bp % 100);
    if (fraction === 0)
        return `${String(whole)}%`;
    return `${String(whole)}.${String(fraction).padStart(2, "0").replace(/0$/, "")}%`;
}
/**
 * The refusal when one discount arrives stated twice.
 *
 * ONE SENTENCE, SHARED BY ALL FOUR PLACES IT CAN HAPPEN — a line and the whole
 * document, on an invoice and on a quotation — so the owner reads one fact about
 * one mistake rather than four drafts of it. `services/documents.ts` raises the
 * identical sentence as a 400 for the callers that build their input in code
 * (conversion §7.1, duplicate §11.3) and never pass through a schema.
 */
export const DISCOUNT_PERCENT_AND_AMOUNT = "Give the discount as a percentage or as an amount, not both — the server works the percentage out to the paisa and records that figure, so two answers would mean one of them being dropped without saying so";
/**
 * `discountPercentBp` and `discountPaise` may not both be stated for the same
 * discount, and neither may sit on a group header.
 *
 * Factored out because the rule applies to a LINE and to the DOCUMENT, on an
 * invoice and on a quotation, and a hand-kept second copy is how one of the four
 * ends up silently preferring the percentage.
 */
function refineOneDiscountStatement(discount, ctx) {
    if (discount.discountPercentBp !== undefined && discount.discountPaise !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["discountPercentBp"],
            message: DISCOUNT_PERCENT_AND_AMOUNT,
        });
    }
}
export const DiscountableDocumentLineBaseSchema = DocumentLineBaseSchema.extend({
    /**
     * A discount on THIS line, in integer paise, applied BEFORE tax. Absent means
     * no discount, which is not the same request as a discount of zero — it takes
     * the code path an undiscounted document has always taken.
     *
     * Must be absent or zero on a group header (checked below): a header carries
     * no value, so there is nothing on it to reduce.
     */
    discountPaise: PaiseSchema.optional(),
    /**
     * The SAME discount, stated as a percentage instead — basis points, `1000` for
     * 10%. Mutually exclusive with `discountPaise` above.
     *
     * OF WHAT, EXACTLY: this line's GROSS taxable value, `qty × rate` by pricing
     * mode, before any discount. The server resolves it through `divRoundHalfUp`
     * at save and stores the resulting paise as the discount; this figure is kept
     * beside it only to record how it was reached, and prints in the line's
     * `Less discount (10%) 1,500.00` note.
     */
    discountPercentBp: DiscountPercentBpSchema.optional(),
});
export const DiscountableDocumentLineSchema = DiscountableDocumentLineBaseSchema.superRefine((line, ctx) => {
    refineLineByPricingMode(line, ctx);
    refineOneDiscountStatement(line, ctx);
    if (line.isGroupHeader === true &&
        ((line.discountPaise ?? 0) > 0 || line.discountPercentBp !== undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["discountPaise"],
            message: "A group header contributes no value to the totals, so there is nothing on it to discount — put the discount on the lines underneath it",
        });
    }
});
/**
 * The whole-document discount, for the header of an invoice or a quotation.
 *
 * IT IS NOT SUBTRACTED FROM THE TOTAL. The lines carry different GST rates (18%
 * fabrication, 5%/12%/18% works contract — §8.1), so the server apportions it
 * across them in proportion to their post-line-discount taxable values and
 * recomputes each line's tax at its own rate. Taking it off the grand total
 * instead would print a wrong CGST and a wrong SGST figure on a legal document.
 *
 * BOTH KINDS MAY COEXIST: per-line discounts come off first, and this one is
 * apportioned over what remains.
 */
const DocumentDiscountPaiseSchema = PaiseSchema.optional();
/**
 * The same whole-document discount, stated as a percentage — basis points.
 * Mutually exclusive with `discountPaise` on the same document.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * OF WHAT, EXACTLY: THE SUBTOTAL AFTER THE PER-LINE DISCOUNTS.
 *
 * This is the question a reader will have, and getting it wrong would make the
 * percentage form quietly disagree with the amount form of the same discount.
 * The amount version is already applied in that order — per-line discounts come
 * off first, and the whole-document one is apportioned over what REMAINS (see
 * the note above, and `computeTotals` pass 3b). So "10% off the job" resolves
 * against the same base the ₹1,000 would have been taken from, and a document
 * where the owner types 10% and one where he types the rupees that 10% comes to
 * store, tax and print identical figures.
 *
 * The alternative — 10% of the GROSS — would exceed what is left to discount on
 * a document already carrying heavy per-line reductions, and the owner would be
 * refused a discount he had every right to give.
 * ═══════════════════════════════════════════════════════════════════════════
 */
const DocumentDiscountPercentBpSchema = DiscountPercentBpSchema.optional();
/**
 * Cross-line checks for the two-level hierarchy (spec §3):
 *   - keys are unique
 *   - every `parentKey` resolves to a line in the same document
 *   - a parent is always a group header, so it is excluded from the totals —
 *     this is exactly what prevents a parent amount being double-counted
 *   - no line is its own parent
 */
export function refineDocumentLines(lines, ctx) {
    const keyToIndex = new Map();
    lines.forEach((line, index) => {
        if (line.key === undefined)
            return;
        const existing = keyToIndex.get(line.key);
        if (existing !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["lines", index, "key"],
                message: `Duplicate line key "${line.key}" (also used by line ${existing + 1})`,
            });
            return;
        }
        keyToIndex.set(line.key, index);
    });
    lines.forEach((line, index) => {
        const parentKey = line.parentKey;
        if (parentKey === null || parentKey === undefined)
            return;
        if (line.key !== undefined && line.key === parentKey) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["lines", index, "parentKey"],
                message: "A line cannot be its own parent",
            });
            return;
        }
        const parentIndex = keyToIndex.get(parentKey);
        if (parentIndex === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["lines", index, "parentKey"],
                message: `parentKey "${parentKey}" does not match any line in this document`,
            });
            return;
        }
        const parent = lines[parentIndex];
        if (parent !== undefined && parent.isGroupHeader !== true) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["lines", index, "parentKey"],
                message: "A parent line must be a group header — a parent contributes nothing to the totals and its rate is derived from its children",
            });
        }
    });
}
/** Challans and credit notes — no discount field exists on these lines (§15(3)(a)). */
const LinesSchema = z.array(DocumentLineSchema).min(1, "A document needs at least one line");
/** Invoices and quotations — the only two documents a discount may appear on. */
const DiscountableLinesSchema = z
    .array(DiscountableDocumentLineSchema)
    .min(1, "A document needs at least one line");
/* -------------------------------------------------------------------------- */
/* Customer                                                                    */
/* -------------------------------------------------------------------------- */
export const CustomerCreateBaseSchema = z.object({
    name: z.string().trim().min(1, "Customer name is required").max(191, "Customer name is too long"),
    /** Absent for an unregistered / B2C customer (spec §3). */
    gstin: GstinSchema.optional(),
    /**
     * Rule 114B (income tax, 2026-08-15): a single sale over ₹2,00,000 must quote
     * the buyer's PAN even when they hold no GSTIN. FOR THE NO-GSTIN CASE ONLY —
     * a registered buyer's PAN is derived from the GSTIN (characters 3–12) at
     * print time, and the superRefine below refuses a stored copy beside a GSTIN
     * so the two can never disagree on a legal document.
     */
    pan: PanSchema.optional(),
    /**
     * Place of supply. Derived from the GSTIN for a registered customer; typed by
     * the user otherwise — which is why it is required when there is no GSTIN
     * (spec §4, "State code source of truth").
     */
    stateCode: StateCodeSchema.optional(),
    addressLine1: optionalText(191),
    addressLine2: optionalText(191),
    city: optionalText(100),
    pincode: PincodeSchema.optional(),
    phone: PhoneSchema.optional(),
    email: z.string().trim().email("Email address is not valid").max(191).optional(),
    /** Lupin's code for this business, printed on quotations (spec §7.4). */
    vendorCode: optionalText(64),
    /** Default "Kind Attn to:" contact; still overridable per document (§7.4). */
    defaultContactName: optionalText(191),
});
/**
 * `POST /customers` (spec §6).
 *
 * Only ONE rule is enforced here, and it is the one the client alone can satisfy:
 * a customer with no GSTIN must name a state, because without it the engine
 * cannot choose CGST+SGST over IGST (§4).
 *
 * A `stateCode` that CONTRADICTS the GSTIN is deliberately NOT an error. Spec §4
 * makes the state code of a registered customer a DERIVED value — "first 2 chars
 * of customer GSTIN if registered" — and §6 puts every derived value under
 * server authority. So the server recomputes it and ignores whatever the client
 * sent, exactly as it does with totals and document numbers; the API answers
 * with a `warnings` entry saying which value it used, so a genuine typo is still
 * visible to the person who made it. Rejecting instead would mean trusting the
 * client's opinion far enough to argue with it.
 */
export const CustomerCreateSchema = CustomerCreateBaseSchema.superRefine((customer, ctx) => {
    if (customer.pan !== undefined &&
        customer.pan !== null &&
        customer.gstin !== undefined &&
        customer.gstin !== null &&
        customer.gstin !== "") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["pan"],
            message: "This customer has a GSTIN, and the PAN inside it is what prints — leave the PAN field empty.",
        });
    }
    if (customer.gstin === undefined && customer.stateCode === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["stateCode"],
            message: "State is required for a customer without a GSTIN — it decides CGST+SGST vs IGST",
        });
    }
});
/** `clearable()` around the create schema's own field, so the caps never drift. */
function clearableField(field) {
    return clearable(field.unwrap());
}
export const CustomerUpdateBaseSchema = CustomerCreateBaseSchema.partial().extend({
    /**
     * `null` un-registers the customer (B2C). The server then falls back to the
     * stored `state_code`, so clearing a GSTIN never leaves a row without a place
     * of supply.
     */
    gstin: clearableField(CustomerCreateBaseSchema.shape.gstin),
    /**
     * CLEARABLE, LIKE EVERY OTHER OPTIONAL FIELD — it was the one omission here,
     * and the omission inverted the field's semantics.
     *
     * `.partial()` above makes `pan` optional but NOT nullable, so `pan: null`
     * was a 400 ("expected string") while OMITTING `pan` fell through to the
     * route's `input.pan ?? null` and silently wiped a stored one. Exactly
     * backwards: the request that meant "leave it alone" cleared it, and the
     * request that meant "clear it" was refused.
     *
     * A buyer's PAN is Rule 114B evidence for a sale over ₹2,00,000. Losing it
     * without being asked is losing a record the business is required to hold.
     */
    pan: clearableField(CustomerCreateBaseSchema.shape.pan),
    addressLine1: clearableField(CustomerCreateBaseSchema.shape.addressLine1),
    addressLine2: clearableField(CustomerCreateBaseSchema.shape.addressLine2),
    city: clearableField(CustomerCreateBaseSchema.shape.city),
    pincode: clearableField(CustomerCreateBaseSchema.shape.pincode),
    phone: clearableField(CustomerCreateBaseSchema.shape.phone),
    email: clearableField(CustomerCreateBaseSchema.shape.email),
    vendorCode: clearableField(CustomerCreateBaseSchema.shape.vendorCode),
    defaultContactName: clearableField(CustomerCreateBaseSchema.shape.defaultContactName),
    /**
     * The ONLY way a customer is ever removed (CLAUDE.md, §3): customers are never
     * hard-deleted, because a deleted row would orphan the invoices that legally
     * have to keep naming their buyer for 72 months (§14.3).
     */
    isActive: z.boolean().optional(),
});
/**
 * `PUT /customers/:id` (spec §6). Partial by design — the SPA saves one section
 * at a time — so the "state is required without a GSTIN" rule cannot live here:
 * the missing half of the answer is in the stored row, which only the server can
 * see. `resolveCustomerState` in apps/api applies it after the merge.
 */
export const CustomerUpdateSchema = CustomerUpdateBaseSchema.strict().superRefine((value, ctx) => {
    if (Object.keys(value).length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Send at least one field to update" });
    }
});
/**
 * `GET /customers?q=&active=&limit=&offset=` (spec §6).
 *
 * `q` searches name, phone and GSTIN. Case-insensitivity is NOT expressed here
 * and must not be added in the query layer either: the columns are
 * `utf8mb4_unicode_ci`, so MariaDB already compares case-insensitively, and a
 * `LOWER(name) LIKE ...` wrapper would only succeed in making the `name` index
 * unusable (CLAUDE.md, §1).
 *
 * `active` is unset by default, meaning "both". Defaulting it to `true` would
 * quietly hide archived customers from a search that the user typed a name into
 * and then explain nothing.
 */
export const CustomerListQuerySchema = z
    .object({
    q: optionalQuery(z.string().trim().max(191, "Search text is too long").optional()),
    active: optionalQuery(BooleanQuerySchema.optional()),
    limit: optionalQuery(z.coerce
        .number()
        .int("limit must be a whole number")
        .min(1, "limit must be at least 1")
        .max(200, "limit cannot exceed 200")
        .default(50)),
    offset: optionalQuery(z.coerce
        .number()
        .int("offset must be a whole number")
        .min(0, "offset cannot be negative")
        .default(0)),
})
    .strict();
/**
 * `POST /customers/validate-gstin` (spec §6).
 *
 * Deliberately NOT `GstinSchema`: the whole purpose of the endpoint is to answer
 * `{ valid: false }` for a bad GSTIN, so validating the input with the strict
 * schema would turn every "is this valid?" question into a 400 and the endpoint
 * would never once return the answer it exists to give. The cap is only there to
 * stop a megabyte of text reaching the regex.
 */
export const GstinCheckSchema = z
    .object({
    gstin: z
        .string()
        .trim()
        .min(1, "GSTIN is required")
        .max(20, "GSTIN must be 20 characters or fewer"),
})
    .strict();
/* -------------------------------------------------------------------------- */
/* Quotation (§8.3) — Phase 2 extends                                          */
/* -------------------------------------------------------------------------- */
export const QuotationCreateBaseSchema = z.object({
    customerId: IdSchema,
    date: IsoDateSchema,
    validUntil: IsoDateSchema.optional(),
    /** "Kind Attn to:" / "Dear <contact> sir" on the printed quotation. */
    attnName: optionalText(191),
    /** "Subject: <job description>" line (§8.3). */
    subject: optionalText(500),
    /**
     * "PO ref. no" — printed in the footer block of the existing quotation layout
     * (§9.3), alongside the vendor code. The customer's purchase-order reference
     * when the quote is raised against one; blank on a cold quote.
     */
    poRefNo: optionalText(64),
    /**
     * "Item no" — the customer's own item/line number for this job, printed in
     * §9.3's references footer beside the P.O. number.
     *
     * TYPEABLE SINCE 2026-08-15, AND STILL A WRITE-IN WHEN IT IS NOT TYPED. The
     * footer used to print an empty labelled line with a rule under it, because
     * the original Excel sheet leaves it for a pen — transcribed faithfully
     * without asking whether the owner would rather type it. He would. Omitting
     * it keeps the blank rule, so a quotation raised before this field existed,
     * or one where the customer gave no item number, prints exactly as it always
     * did; the renderers must never print `""` where the rule used to be.
     *
     * PER QUOTATION, unlike the vendor code — that one is the customer's standing
     * code for this business, lives on `customers.vendor_code`, and is copied onto
     * the document at save time (§4). Nothing here may set it.
     *
     * 64 to match `quotations.item_no @db.VarChar(64)`, same as `poRefNo` above.
     */
    itemNo: optionalText(64),
    /**
     * The existing quotations print "SGST 9% EXTRA" instead of computing tax, so
     * TAX_EXTRA is the default and matches current practice (§8.3).
     */
    taxDisplayMode: TaxDisplayModeSchema.default(TaxDisplayMode.TAX_EXTRA),
    lines: DiscountableLinesSchema,
    /**
     * §15(3)(a) — a discount on the quotation as a whole, in integer paise. See
     * {@link DiscountableDocumentLineBaseSchema} for why it is money and not a
     * percentage, and why the server apportions it rather than subtracting it.
     *
     * ON A QUOTATION IT IS VISIBLE WITH NO EXTRA WORK. Under the default
     * `TAX_EXTRA` display mode the printed "G. Total" IS the pre-tax subtotal
     * (§9.3), so a discount taken before tax moves the headline figure the
     * customer reads.
     */
    discountPaise: DocumentDiscountPaiseSchema,
    /**
     * §15(3)(a) — the same whole-quotation discount stated as a percentage, in
     * basis points. Mutually exclusive with `discountPaise` above; see
     * {@link DocumentDiscountPercentBpSchema} for what it is a percentage OF.
     */
    discountPercentBp: DocumentDiscountPercentBpSchema,
    notes: optionalText(5000),
    terms: optionalText(5000),
});
/**
 * A quotation cannot expire before it is written. `valid_until` is the only
 * date on the document besides its own, and the two are typed on the same
 * screen, so a transposed pair is a realistic mistake that produces a document
 * the customer is told is already dead.
 */
function refineValidUntil(doc, ctx) {
    if (doc.date === undefined || doc.validUntil === undefined)
        return;
    // Both are ISO-8601 and already validated as real calendar days, so a
    // lexicographic comparison of the `YYYY-MM-DD` head is the whole answer — no
    // Date is constructed, and therefore no machine-local timezone is involved.
    if (doc.validUntil.slice(0, 10) < doc.date.slice(0, 10)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["validUntil"],
            message: "\"Valid until\" cannot be earlier than the quotation date",
        });
    }
}
export const QuotationCreateSchema = QuotationCreateBaseSchema.superRefine((doc, ctx) => {
    refineDocumentLines(doc.lines, ctx);
    refineValidUntil(doc, ctx);
    refineOneDiscountStatement(doc, ctx);
});
/**
 * `PUT /quotations/:id` (§7 — "only if DRAFT/SENT").
 *
 * A FULL REPLACEMENT of the editable document, not a patch. Lines are an
 * ordered tree, so "merge these three lines into the seven already stored" has
 * no meaning that a client and a server would agree on; the editor holds the
 * whole document and sends the whole document back. An omitted optional header
 * field therefore CLEARS that field, exactly as with any other PUT.
 *
 * Two exceptions, and they are the reason this is not simply the create schema:
 *  - `customerId` may be omitted to keep the buyer the document already names.
 *    Sending a different one re-freezes the snapshot from that customer (§4).
 *  - `date` may be omitted to keep the quotation's own date. Sending a new one
 *    RE-RESOLVES every line's GST rate as of that date (§8.7), which is correct
 *    and deliberate — the document is not yet issued.
 *
 * What this schema CANNOT carry, structurally: `status` (that is
 * `POST /quotations/:id/status`, which validates the transition), the quotation
 * number (allocated once, §6), and any money total (§7 server authority —
 * `z.object` strips them before a handler could read one).
 */
export const QuotationUpdateBaseSchema = QuotationCreateBaseSchema.extend({
    customerId: IdSchema.optional(),
    date: IsoDateSchema.optional(),
});
export const QuotationUpdateSchema = QuotationUpdateBaseSchema.superRefine((doc, ctx) => {
    refineDocumentLines(doc.lines, ctx);
    refineValidUntil(doc, ctx);
    refineOneDiscountStatement(doc, ctx);
});
/* -------------------------------------------------------------------------- */
/* Quotation status (§4 statuses, §7.1 conversion)                             */
/* -------------------------------------------------------------------------- */
/**
 * The statuses a human may set with `POST /quotations/:id/status`.
 *
 * `CONVERTED` and `PARTIALLY_CONVERTED` are deliberately ABSENT. Those two are
 * facts about invoices, not opinions about a quotation: §7.1 sets them — and
 * only §7.1 sets them — as part of the same transaction that creates the
 * invoice, links `source_quote_id` / `converted_invoice_id` both ways, and
 * decides whether any line remains uninvoiced. A quotation hand-marked
 * CONVERTED would claim an invoice that does not exist, and would then be
 * refused every edit for a reason nobody can trace back to a document.
 *
 * `VOID` is absent for a DIFFERENT reason (§4, added 2026-08-13), and its
 * absence here is load-bearing in BOTH directions:
 *  - nothing may be voided THROUGH this endpoint, because voiding requires a
 *    mandatory reason and writes `voided_at` / `voided_by_user_id` /
 *    `void_reason` together with the status. A bare `{ status: "VOID" }` would
 *    produce a voided document with none of that — precisely the "voided with no
 *    stated reason, indistinguishable from a bug six months later" §4 refuses.
 *    `POST /quotations/:id/void { reason }` is the only door.
 *  - nothing may be moved OUT of `VOID` either, because `VOID` is TERMINAL (§4).
 *    Un-voiding would put a number back into circulation after the owner has
 *    already told a customer it was cancelled.
 *
 * Excluding all three HERE, in the schema, means the refusal is a 400 naming the
 * field rather than a rule buried in a handler.
 */
export const MANUAL_QUOTATION_STATUSES = [
    "DRAFT",
    "SENT",
    "APPROVED",
    "REJECTED",
    "EXPIRED",
];
/**
 * The two statuses only conversion may write (§7.1).
 *
 * WRITTEN OUT RATHER THAN DERIVED AS "everything that is not manual", which is
 * what this used to be. That complement was correct while `CONVERTED` and
 * `PARTIALLY_CONVERTED` were the only non-manual values — and it silently
 * stopped being correct the moment §4 added `VOID` (2026-08-13), because `VOID`
 * would have joined this list and the 400 below would have told the owner that
 * "PARTIALLY_CONVERTED and CONVERTED and VOID are set only by converting the
 * quotation into an invoice". Voiding has nothing to do with conversion; that
 * sentence would have sent him looking for an invoice that does not exist.
 *
 * The exhaustiveness the complement used to give for free is recovered by
 * {@link _EveryQuotationStatusIsAccountedFor} below, which fails the typecheck
 * if a future status belongs to none of the three groups.
 */
export const CONVERSION_ONLY_QUOTATION_STATUSES = [
    "PARTIALLY_CONVERTED",
    "CONVERTED",
];
/** Set by `POST /quotations/:id/void` alone, and never left again (§4). */
export const VOID_ONLY_QUOTATION_STATUSES = ["VOID"];
export const ManualQuotationStatusSchema = z.enum(MANUAL_QUOTATION_STATUSES, {
    errorMap: () => ({
        message: `Status must be one of ${MANUAL_QUOTATION_STATUSES.join(", ")}. ${CONVERSION_ONLY_QUOTATION_STATUSES.join(" and ")} are set only by converting the quotation into an invoice, and VOID only by cancelling it with a reason`,
    }),
});
export const QuotationStatusUpdateSchema = z
    .object({
    status: ManualQuotationStatusSchema,
    /**
     * Why — kept in `audit_log`, never on the quotation (§13). "Customer went
     * with another quote" is the sort of thing that is worth having six months
     * later and has no column of its own.
     */
    reason: optionalText(500),
    /**
     * ═════════════════════════════════════════════════════════════════════════
     * HOW IT WENT OUT. Only meaningful with `status: "SENT"`, and the reason
     * this route is now the ONLY way a quotation is marked sent.
     *
     * THERE WERE THREE CONTROLS AND THE OWNER ASKED FOR ONE (2026-08-20): "we
     * already have mark as sent, so remove sent on whatsapp and record as sent".
     * The catch is that they were not the same act. "Mark as sent" moved the
     * STATUS and nothing else; the other two posted to `POST /:id/record-sent`,
     * which stamped `sent_at` AND `sent_channel`. Deleting them and keeping the
     * status button would have quietly dropped both — the ageing clock behind
     * §11.2's "waiting for reply", and the answer to "how did we send it?" that
     * a disputed delivery turns on.
     *
     * So the channel moved onto the surviving control. `RECORDED` is the default
     * a bare status change means — a person asserting the customer has it, which
     * is exactly what "mark as sent" claims — and the WhatsApp share passes
     * `WHATSAPP` because that share genuinely happened.
     *
     * IT CANNOT CLAIM `EMAIL` OR `LINK`. Those are written only by the routes
     * that OBSERVE them (`POST /:id/email` after SMTP accepts;
     * the portal when the customer opens the page), which is what
     * {@link ClaimableQuotationSendChannelSchema} exists to enforce — a person
     * and a machine asserting a send are different facts and the column has to
     * keep them tellable apart.
     *
     * OPTIONAL, so every existing caller keeps working and an offline replay of
     * a body written before today still means what it meant.
     * ═════════════════════════════════════════════════════════════════════════
     */
    channel: ClaimableQuotationSendChannelSchema.optional(),
})
    .strict();
/* -------------------------------------------------------------------------- */
/* Quotation → invoice conversion (§7.1)                                       */
/* -------------------------------------------------------------------------- */
/**
 * `POST /quotations/:id/convert` — the whole request body (§7.1).
 *
 * IT IS TINY ON PURPOSE. §7.1: "Conversion opens a single small prompt asking
 * only for these" — the customer's S.O. number and its date. Everything else the
 * invoice needs is already on the quotation (customer, snapshot, lines,
 * descriptions, SAC/HSN, units, rates, terms, attn_name) or is the server's to
 * generate (the invoice number, today's date, the tax figures, the two-level
 * restructuring). A field on this schema that duplicates one of those would be a
 * second place for the invoice to disagree with the quotation it came from.
 *
 * NOT ON IT, deliberately:
 *  - `date`. §7.1 fixes the invoice date as TODAY, three weeks or so after the
 *    quotation; letting the client choose would reopen the FY-rollover hazard
 *    that §6 spends a page on.
 *  - the invoice number (§6 — allocated, never proposed).
 *  - any money total (§7 — `z.object` strips them before a handler could read
 *    one), and any tax field: the rates are re-resolved as of the invoice date.
 *  - the resulting quotation status. CONVERTED / PARTIALLY_CONVERTED are decided
 *    by whether any line is left uninvoiced, which only the server can know.
 */
export const QuotationConvertBaseSchema = z.object({
    /**
     * The customer's SAP sales order number (§9.2 — mandatory on Lupin's invoices).
     *
     * OPTIONAL, and that is a requirement rather than a convenience: §7.1 says in
     * as many words "Do NOT block conversion if absent — save as DRAFT and flag
     * 'S.O. number needed'". It arrives with the purchase order weeks after the
     * quotation and cannot be pre-filled.
     */
    soNumber: optionalText(64),
    soDate: IsoDateSchema.optional(),
    /**
     * PARTIAL CONVERSION (§7.1): the `quotation_lines.id`s to bill now. Omit to
     * bill everything that has not been invoiced yet — the ordinary one-tap case.
     *
     * Ids rather than indexes because a quotation converts in several goes ("one
     * quotation → multiple invoices"), and a positional selection would silently
     * mean something different on the second invoice.
     */
    lineIds: z
        .array(IdSchema)
        .min(1, "Select at least one line to invoice, or omit lineIds to bill the whole quotation")
        .max(200, "That is more lines than a document can carry")
        .optional(),
});
export const QuotationConvertSchema = QuotationConvertBaseSchema.strict().superRefine((body, ctx) => {
    // A date with no number is an S.O. that cannot be quoted back to the customer,
    // and it would print an "S.O. Date" row above an empty "S.O. No." row (§9.2).
    if (body.soDate !== undefined && (body.soNumber === undefined || body.soNumber === "")) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["soNumber"],
            message: "Enter the S.O. number as well as its date — the invoice prints them together",
        });
    }
    if (body.lineIds !== undefined) {
        const seen = new Set();
        body.lineIds.forEach((id, index) => {
            const key = String(id);
            if (seen.has(key)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["lineIds", index],
                    message: `Line ${key} is selected twice — each line can only be invoiced once`,
                });
            }
            seen.add(key);
        });
    }
});
/* -------------------------------------------------------------------------- */
/* Quotation list (§7 — GET /quotations?status=&customerId=&from=&to=)         */
/* -------------------------------------------------------------------------- */
export const QUOTATION_LIST_DEFAULT_LIMIT = 25;
export const QUOTATION_LIST_MAX_LIMIT = 100;
/**
 * `from` / `to` are INCLUSIVE calendar days matched against `quote_date`, which
 * is a `@db.Date`. They are kept as strings here for the reason every date in
 * this package is (§13): the boundary must be interpreted in Asia/Kolkata by the
 * server, never constructed in whatever zone the machine happens to run in.
 *
 * Every filter is optional and unset means "no filter" — §11.2 taps through to
 * this list pre-filtered ("Quotations waiting for reply: 3" → `status=SENT`),
 * and the same screen with nothing selected must show everything.
 */
export const QuotationListQuerySchema = z
    .object({
    /**
     * Free text over the quotation's OWN reference numbers — `quote_number` and
     * the customer's `po_ref_no`.
     *
     * ADDED FOR THE SCANNER (§9 amendment). A printed quotation carries a Code
     * 128 barcode of its number, and scanning it has to resolve to a document;
     * without this the only lookup was by id, which nothing on paper carries.
     *
     * It does NOT search the customer name, for the same reason the invoice
     * endpoint does not: the buyer lives in `customer_snapshot`, a text-backed
     * JSON column, and CLAUDE.md forbids querying inside one. That is what
     * `customerId` is for.
     */
    q: optionalQuery(z.string().trim().max(64, "Search text is too long").optional()),
    /**
     * Column sorting for the register (§11.2 amendment, 2026-08-15). A CLOSED
     * SET of names, never a raw column — see `invoiceListOrderBy` in the API for
     * why `number` maps to `id` rather than to the number string.
     */
    sort: optionalQuery(z.enum(["number", "date", "customer", "subject", "status", "total"]).optional()),
    dir: optionalQuery(z.enum(["asc", "desc"]).optional()),
    status: optionalQuery(QuotationStatusSchema.optional()),
    customerId: optionalQuery(z.coerce
        .number()
        .int("customerId must be a whole number")
        .positive("customerId must be positive")
        .optional()),
    from: optionalQuery(IsoDateSchema.optional()),
    to: optionalQuery(IsoDateSchema.optional()),
    /**
     * Inclusive bounds on the amount, in integer paise — register parity with
     * the invoice list (§11.2), which has carried these since it was built.
     *
     * PAISE, NOT RUPEES, exactly as on the invoice endpoint: every money value
     * that crosses this API is paise, and one field that quietly took rupees
     * would be the most expensive inconsistency in the system. "Quotations over
     * three lakh" is `minTotalPaise=30000000`.
     *
     * ─────────────────────────────────────────────────────────────────────────
     * WHICH TOTAL THESE BOUND IS NOT `grand_total_paise` (§9.3). THE SERVER
     * MATCHES THEM AGAINST THE FIGURE THE REGISTER PRINTS, which is
     * `printedGrandTotalPaise` — the PRE-TAX subtotal under `TAX_EXTRA` and the
     * tax-inclusive grand total under `TAX_COMPUTED`. See `quotationDisplay` in
     * the API's quotation service for the derivation, and `quotationListWhere`
     * for the clause.
     *
     * That is not a nicety. TAX_EXTRA is the schema default and the majority of
     * rows: a quotation whose row reads "₹6,500.00 + tax extra" stores a grand
     * total of ₹7,670.00, so a bound applied to `grand_total_paise` would drop
     * it out of "at most ₹6,500" and pull it into "at least ₹7,000" — the exact
     * opposite of what the screen says both times. The owner would call the
     * filter broken and he would be right.
     * ─────────────────────────────────────────────────────────────────────────
     */
    minTotalPaise: optionalQuery(z.coerce.number().int("minTotalPaise must be a whole number of paise").min(0).optional()),
    maxTotalPaise: optionalQuery(z.coerce.number().int("maxTotalPaise must be a whole number of paise").min(0).optional()),
    limit: optionalQuery(z.coerce
        .number()
        .int("limit must be a whole number")
        .min(1, "limit must be at least 1")
        .max(QUOTATION_LIST_MAX_LIMIT, `limit cannot exceed ${QUOTATION_LIST_MAX_LIMIT}`)
        .default(QUOTATION_LIST_DEFAULT_LIMIT)),
    offset: optionalQuery(z.coerce
        .number()
        .int("offset must be a whole number")
        .min(0, "offset cannot be negative")
        .default(0)),
})
    .strict()
    .superRefine((query, ctx) => {
    if (query.from !== undefined && query.to !== undefined && query.to < query.from) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["to"],
            message: "\"to\" cannot be earlier than \"from\"",
        });
    }
    /*
     * A range whose ends are the wrong way round matches nothing, and an empty
     * register is indistinguishable from "you have no quotations". Saying so is
     * the difference between a typo the owner can fix and a silence he cannot —
     * the same reason `to` before `from` is refused above, and the same wording
     * the invoice endpoint uses so one mistake never gets two sentences.
     */
    if (query.minTotalPaise !== undefined &&
        query.maxTotalPaise !== undefined &&
        query.minTotalPaise > query.maxTotalPaise) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["maxTotalPaise"],
            message: "maxTotalPaise cannot be smaller than minTotalPaise",
        });
    }
});
/* -------------------------------------------------------------------------- */
/* Invoice (§8.2) — Phase 2 extends                                            */
/* -------------------------------------------------------------------------- */
export const InvoiceCreateBaseSchema = z.object({
    customerId: IdSchema,
    date: IsoDateSchema,
    /** Set when the invoice was produced by converting a quotation (§6). */
    sourceQuoteId: IdSchema.nullish(),
    /**
     * Header fields the real template prints as four value+date rows (§8.2).
     * The S.O. number is Lupin's SAP sales order and is mandatory on their
     * invoices — enforce that per-customer in Phase 2, not globally here.
     */
    soNumber: optionalText(64),
    soDate: IsoDateSchema.optional(),
    challanNumber: optionalText(64),
    challanDate: IsoDateSchema.optional(),
    quotationRef: optionalText(64),
    quotationRefDate: IsoDateSchema.optional(),
    /**
     * §17.7 — the deliveries this bill CLOSES. `delivery_challans.id`s.
     *
     * ═══════════════════════════════════════════════════════════════════════════
     * THE TRIGGER FOR BILLING A CHALLAN IS ON THE INVOICE, NOT ON THE CHALLAN
     * (decided 2026-08-16). Inside the same transaction that allocates the invoice
     * number, each of these challans gets `linked_invoice_id` and moves to
     * `INVOICED`. There is deliberately NO "mark as billed" button on the challan
     * screens: two ways to say the same thing would let them disagree, and the
     * disagreement would always favour the version nobody checked.
     *
     * IT IS NOT `challanNumber`, WHICH IS THE FIELD ABOVE AND STAYS FREE TEXT
     * (§6). That column holds the printed REFERENCE — most often the CUSTOMER's
     * own challan number, naming a document that is not ours — and nothing may
     * ever link from a text match on it. This array is the only thing that closes
     * a delivery. When it is present the server copies the linked challans'
     * numbers into the text so the header prints them; the FK is what the app
     * navigates, the text is what the page says.
     *
     * MANY CHALLANS TO ONE INVOICE. Three site drops are routinely billed on one
     * monthly invoice, which is why `DeliveryChallan.linkedInvoiceId` is not
     * `@unique` and why this is an array rather than a single id.
     *
     * OMITTED IS THE ORDINARY CASE — most bills close no delivery at all, and an
     * empty array means the same thing as an absent key.
     * ═══════════════════════════════════════════════════════════════════════════
     */
    challanIds: z
        .array(IdSchema)
        .min(1, "Select at least one delivery challan, or leave the field empty")
        .max(50, "That is more deliveries than one invoice can close")
        /*
         * Listed twice would be harmless in the write — the `updateMany` is keyed on
         * the id set — but it would print the same challan number twice in a 64-char
         * header field and read as two deliveries in the audit log. Refusing is
         * cheaper than explaining. Same rule and same reason as `lineIds` on
         * `QuotationConvertSchema`.
         */
        .refine((ids) => new Set(ids.map((id) => String(id))).size === ids.length, "The same delivery challan is listed twice — each delivery is billed once")
        .optional(),
    /** "Kind Attn to:" on the buyer block. */
    attnName: optionalText(191),
    /** Which copy to render first (§8.2). */
    copyType: CopyTypeSchema.default(CopyType.ORIGINAL),
    lines: DiscountableLinesSchema,
    /**
     * §15(3)(a) — a discount on the whole bill, in integer paise, applied BEFORE
     * tax. See {@link DiscountableDocumentLineBaseSchema}.
     *
     * IT IS ON THE CREATE ONLY, and there is deliberately no way to add, change or
     * remove one afterwards. An issued invoice is not amendable — `invoice.edit`
     * does not exist as a capability for anyone (§2.1) — and a discount changes
     * the taxable value, the tax and therefore the return. Reducing a bill after
     * it has been issued is a CREDIT NOTE (§17.3, §15(3)(b): a post-supply
     * discount needs a prior agreement and ITC reversal by the recipient), which
     * is why `InvoiceDraftUpdateSchema` is `.strict()` and does not list this.
     */
    discountPaise: DocumentDiscountPaiseSchema,
    /**
     * §15(3)(a) — the same whole-bill discount stated as a percentage, in basis
     * points, resolved to paise by the server at save and stored beside the money
     * it produced. Mutually exclusive with `discountPaise` above.
     *
     * ON THE CREATE ONLY, for exactly the reason `discountPaise` is: an issued
     * invoice is not amendable, and this is provenance for a figure that was
     * written once. `InvoiceDraftUpdateSchema` is `.strict()` and does not list it.
     */
    discountPercentBp: DocumentDiscountPercentBpSchema,
    notes: optionalText(5000),
    terms: optionalText(5000),
});
export const InvoiceCreateSchema = InvoiceCreateBaseSchema.superRefine((doc, ctx) => {
    refineDocumentLines(doc.lines, ctx);
    refineOneDiscountStatement(doc, ctx);
});
/* -------------------------------------------------------------------------- */
/* Delivery challans — §17.7, Rule 55 CGST                                      */
/* -------------------------------------------------------------------------- */
/**
 * The document goods travel under when there is NO tax invoice.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * IT CARRIES MONEY AND IS NOT A TAX DOCUMENT. Rule 55(1) requires a taxable
 * value, and a tax rate and amount where the movement is for supply, so the
 * lines are validated by exactly the same `DocumentLineSchema` as an invoice's
 * and the server computes them with the same engine. None of it is ever output
 * tax: nothing here reaches GSTR-3B, and only the SERIAL RANGE is declared, in
 * GSTR-1 (§17.4).
 *
 * WHAT IS ABSENT IS AS DELIBERATE AS WHAT IS HERE. No `challanNumber` — the
 * server allocates it (§6). No `status` — a challan opens OPEN and is advanced
 * through its own routes. No `supplyType` — derived from the two state codes,
 * never asserted by a client. And no `direction`: it is derived from
 * `challanType` by `challanDirection`, because a stored copy could disagree and
 * print the wrong party as the sender of goods (§17.7).
 * ═══════════════════════════════════════════════════════════════════════════
 */
export const ChallanCreateBaseSchema = z.object({
    customerId: IdSchema,
    date: IsoDateSchema,
    /**
     * The legal basis for the movement (Rule 55(1)) — required, with no default.
     *
     * NO DEFAULT ON PURPOSE, unlike `taxDisplayMode` on a quotation. Defaulting to
     * `JOB_WORK_OUT` would silently print "goods sent for job work" on a challan
     * recording a customer's material coming IN, inverting the consigner and
     * consignee blocks on a document that travels with the goods. There is no safe
     * guess, so the caller states it.
     */
    challanType: ChallanTypeSchema,
    /**
     * Whether the goods are expected back. Omitted means "whatever this type
     * usually does" — `defaultIsReturnable(challanType)`, applied server-side, so
     * a client that does not know about the field still gets the right answer.
     */
    isReturnable: z.boolean().optional(),
    expectedReturnDate: IsoDateSchema.optional(),
    /**
     * The vehicle carrying the goods. Rule 55 does not require it on the challan,
     * but the driver is at the gate and nobody can look the number up afterwards.
     */
    vehicleNumber: optionalText(20),
    /**
     * Where the goods are physically going, when that is NOT the customer's
     * registered address — a galvaniser's workshop, a site. Blank means "the
     * address printed above", which is the ordinary case.
     */
    shipToAddress: optionalText(1000),
    /** "Kind Attn to:" on the consignee block. */
    attnName: optionalText(191),
    lines: LinesSchema,
    notes: optionalText(5000),
    terms: optionalText(5000),
});
/**
 * Goods cannot be due back before they leave. The two dates are typed on one
 * screen, so a transposed pair is a realistic mistake — and it produces a
 * challan that is overdue the moment it is printed, which is exactly the signal
 * §17.7 wants to stay trustworthy.
 */
function refineExpectedReturn(doc, ctx) {
    if (doc.date === undefined || doc.expectedReturnDate === undefined)
        return;
    // Lexicographic on the `YYYY-MM-DD` head, as everywhere in this package: no
    // Date is constructed, so no machine-local timezone is involved (§13).
    if (doc.expectedReturnDate.slice(0, 10) < doc.date.slice(0, 10)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["expectedReturnDate"],
            message: '"Expected back" cannot be earlier than the challan date',
        });
    }
}
/**
 * An expected return date on goods nobody expects back is a contradiction, and
 * the honest reading of it is ambiguous — did they mean to tick "returnable", or
 * to clear the date? Refusing makes the caller say which.
 *
 * Only checked when `isReturnable` is EXPLICITLY false. Omitted means "use the
 * type's default", which the server resolves, and a caller who sends a return
 * date while leaving the flag alone is plainly asking for a returnable movement.
 */
function refineReturnableConsistency(doc, ctx) {
    if (doc.isReturnable === false && doc.expectedReturnDate !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["expectedReturnDate"],
            message: "Goods that are not coming back cannot have a return date",
        });
    }
}
export const ChallanCreateSchema = ChallanCreateBaseSchema.superRefine((doc, ctx) => {
    refineDocumentLines(doc.lines, ctx);
    refineExpectedReturn(doc, ctx);
    refineReturnableConsistency(doc, ctx);
});
/**
 * Revising a challan — allowed only while it is OPEN, which the server enforces
 * on the stored status rather than on anything sent here (§17.7).
 *
 * A CHALLAN IS EDITABLE AND AN ISSUED INVOICE IS NOT, and that is not an
 * inconsistency. An invoice is a tax document whose correction path is a credit
 * note (§17.3, CLAUDE.md). A challan is a movement record: the lorry is loaded,
 * the count changes, and the paper has to match what actually went. Once the
 * movement closes — RETURNED, INVOICED or CANCELLED — it freezes, because at
 * that point either the goods are back or a tax document depends on it.
 *
 * `PUT` REPLACES THE LINES WHOLESALE and never touches the number, exactly as
 * the quotation `PUT` does.
 */
export const ChallanUpdateBaseSchema = ChallanCreateBaseSchema.extend({
    /** Editing must not silently move a challan to another buyer's goods. */
    customerId: IdSchema.optional(),
});
export const ChallanUpdateSchema = ChallanUpdateBaseSchema.superRefine((doc, ctx) => {
    refineDocumentLines(doc.lines, ctx);
    refineExpectedReturn(doc, ctx);
    refineReturnableConsistency(doc, ctx);
});
/**
 * Closing a movement by hand — "the goods came back".
 *
 * ONLY `RETURNED` IS SETTABLE THIS WAY, and the exclusions are each for their
 * own reason:
 *
 *   INVOICED  is set by raising the invoice, never by typing it. Claiming it
 *             here would mark a delivery billed with no bill behind it, which is
 *             precisely the unbilled-delivery blindness §17.7 exists to remove.
 *   CANCELLED has its own route, because it takes a mandatory reason.
 *   OPEN      is where a challan starts; going back to it would reopen a
 *             movement the owner has already said finished.
 */
export const ChallanReturnSchema = z
    .object({
    /**
     * When the goods actually came back, which is routinely not today — the
     * lorry returned on Saturday and the entry is made on Monday. Omitted means
     * now.
     */
    returnedAt: IsoDateSchema.optional(),
})
    .strict();
/**
 * Cancelling a challan — a status, never a delete, and the number stays consumed.
 *
 * Challan serial ranges are declared in GSTR-1 (§17.4), so a missing number is a
 * question from the department, exactly as with invoices (§5). The reason is
 * MANDATORY for the same reason it is on a voided quotation (§4): a cancelled
 * document with no stated reason is indistinguishable from a bug six months on,
 * and the whole justification for cancelling rather than deleting is that the
 * record survives and explains itself.
 */
export const ChallanCancelSchema = z
    .object({
    reason: z
        .string()
        .trim()
        .min(1, "A reason is required to cancel a challan")
        .max(500, "Reason is too long"),
})
    .strict();
export const CHALLAN_LIST_DEFAULT_LIMIT = 25;
export const CHALLAN_LIST_MAX_LIMIT = 100;
/**
 * The register's filters — the same shape as the quotation list (§11.2), plus
 * the two questions only a challan raises.
 *
 * `openOnly` IS THE ONE §17.7 CALLS FOR BY NAME: "the home screen should surface
 * open challans with no invoice — unbilled deliveries are exactly how a small
 * fabricator quietly loses money." It is deliberately NOT `status=OPEN`: a
 * RETURNABLE challan is open for as long as the scaffolding is on site and is
 * not money owed, so a home-screen count built on status alone would cry wolf.
 * `openOnly` means OPEN *and* of a type that is waiting to be billed.
 */
export const ChallanListQuerySchema = z
    .object({
    /**
     * Free text over the challan's OWN reference numbers — `challan_number` and
     * `vehicle_number`. Not the customer name: the buyer lives in
     * `customer_snapshot`, a text-backed JSON column, and CLAUDE.md forbids
     * querying inside one. That is what `customerId` is for.
     */
    q: optionalQuery(z.string().trim().max(64, "Search text is too long").optional()),
    sort: optionalQuery(z.enum(["number", "date", "customer", "type", "status", "total"]).optional()),
    dir: optionalQuery(z.enum(["asc", "desc"]).optional()),
    status: optionalQuery(ChallanStatusSchema.optional()),
    challanType: optionalQuery(ChallanTypeSchema.optional()),
    /** OPEN, and of a type that is waiting to be billed. See the block above. */
    openOnly: optionalQuery(BooleanQuerySchema.optional()),
    /** Returnable, still out, and past its expected return date. */
    overdueOnly: optionalQuery(BooleanQuerySchema.optional()),
    customerId: optionalQuery(z.coerce
        .number()
        .int("customerId must be a whole number")
        .positive("customerId must be positive")
        .optional()),
    from: optionalQuery(IsoDateSchema.optional()),
    to: optionalQuery(IsoDateSchema.optional()),
    /**
     * Inclusive bounds on the DECLARED VALUE of the goods, in integer paise —
     * register parity with the invoice and quotation lists (§11.2).
     *
     * THESE BOUND `grand_total_paise` DIRECTLY, and that is correct here for a
     * structural reason rather than a lucky one: `tax_display_mode` exists only
     * on `model Quotation`, so a challan has no second "printed" total that
     * could disagree with the stored one. The register renders
     * `row.grandTotalPaise` unmodified, and this filter matches it.
     *
     * IT IS NOT MONEY ANYONE OWES. Rule 55(1) requires a taxable value on a
     * delivery challan (§17.7), so the figure is real and comparable — but none
     * of it is output tax and none of it reaches GSTR-3B. Anything built on this
     * filter must not be worded as an amount due.
     */
    minTotalPaise: optionalQuery(z.coerce.number().int("minTotalPaise must be a whole number of paise").min(0).optional()),
    maxTotalPaise: optionalQuery(z.coerce.number().int("maxTotalPaise must be a whole number of paise").min(0).optional()),
    limit: optionalQuery(z.coerce
        .number()
        .int("limit must be a whole number")
        .min(1, "limit must be at least 1")
        .max(CHALLAN_LIST_MAX_LIMIT, `limit cannot exceed ${CHALLAN_LIST_MAX_LIMIT}`)
        .default(CHALLAN_LIST_DEFAULT_LIMIT)),
    offset: optionalQuery(z.coerce
        .number()
        .int("offset must be a whole number")
        .min(0, "offset cannot be negative")
        .default(0)),
})
    .strict()
    .superRefine((query, ctx) => {
    if (query.from !== undefined && query.to !== undefined && query.to < query.from) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["to"],
            message: '"to" cannot be earlier than "from"',
        });
    }
    /*
     * Reversed bounds match nothing, and a register showing nothing reads as
     * "there are no challans" rather than "you typed the range backwards". Same
     * refusal, same sentence, as the invoice and quotation endpoints.
     */
    if (query.minTotalPaise !== undefined &&
        query.maxTotalPaise !== undefined &&
        query.minTotalPaise > query.maxTotalPaise) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["maxTotalPaise"],
            message: "maxTotalPaise cannot be smaller than minTotalPaise",
        });
    }
});
/* -------------------------------------------------------------------------- */
/* Credit notes — §17.3, section 34(1) CGST                                     */
/* -------------------------------------------------------------------------- */
/**
 * The document that REDUCES a tax invoice already issued.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY IT EXISTS AT ALL. An issued invoice is never edited — `invoice.edit` does
 * not exist as a capability for anyone (§2.1) — so there are exactly two
 * correction paths and they are not interchangeable. CANCEL withdraws the whole
 * bill while the mistake is fresh. A CREDIT NOTE leaves the bill standing and
 * reduces it, which is what §34 requires once the customer has booked it or
 * GSTR-1 is filed, because cancelling then would contradict a return already
 * submitted.
 *
 * WHAT IS ABSENT HERE IS THE CONTRACT, and every omission is load-bearing:
 *
 *   NO `creditNoteNumber`  — allocated server-side under the company-row lock
 *                            (§5, §6), gap-free, from its own `CRN/26-27/n`
 *                            series.
 *   NO `status`            — a credit note opens ISSUED. There is no draft.
 *   NO `customerId`        — it is READ OFF THE INVOICE. A credit note cannot
 *                            be addressed to anyone but the buyer who was
 *                            billed, and a body field would be a way to say
 *                            otherwise.
 *   NO `placeOfSupply`,
 *   NO `supplyType`        — COPIED from the original invoice, never derived
 *                            from today's customer row (§8.7). A buyer who has
 *                            moved state since must not flip an INTRA_STATE
 *                            bill's CGST+SGST into IGST on the note, or the
 *                            original liability stays un-reversed in two heads
 *                            while a new one is created in a third.
 *   NO totals, NO tax      — §6, as on every other document.
 *   NO NEGATIVE ANYTHING   — amounts are POSITIVE; the document TYPE carries
 *                            the meaning "this reduces". `PaiseSchema` and
 *                            `QtyMilliSchema` on the shared line schema already
 *                            refuse a minus sign, which is why no extra refine
 *                            appears below.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export const CreditNoteCreateBaseSchema = z.object({
    /**
     * THE INVOICE THIS REDUCES — §34's mandatory link, and the single most
     * important field on the document.
     *
     * In the BODY rather than the URL because a credit note is its own register
     * with its own number series, its own list and its own detail screen; the
     * invoice is a property of the note, not the noun it hangs off. The server
     * refuses a DRAFT (nothing was billed) or a CANCELLED invoice (already
     * withdrawn in full), each naming which, and stores both a real foreign key
     * and a frozen copy of the number and date for printing.
     */
    invoiceId: IdSchema,
    /**
     * The credit note's OWN date — today, normally. It is NOT the invoice's date:
     * this document is issued now, takes its number from the series current now,
     * and is declared in the CDNR table of THIS month's GSTR-1.
     *
     * The tax it reverses is still the tax that was charged — the rates come from
     * the original supply's date (§8.7), which the server resolves and the client
     * never sends.
     */
    date: IsoDateSchema,
    /**
     * §34(1) — WHY the bill is being reduced: the rate was wrong, the taxable
     * value was overstated, the goods came back.
     *
     * MANDATORY, unlike the optional `notes` below and unlike a challan's
     * `vehicleNumber`. A credit note reduces output tax liability, and one with no
     * stated reason cannot be defended in an assessment — it is indistinguishable
     * from a mistake six months later, which is the same argument §4 makes for a
     * voided quotation's reason and §17.7 for a cancelled challan's.
     */
    reason: z
        .string()
        .trim()
        .min(1, "A reason is required — section 34(1) asks why the bill is being reduced")
        .max(500, "Reason is too long"),
    /** "Kind Attn to:", defaulted server-side from the invoice's own contact. */
    attnName: optionalText(191),
    /**
     * WHAT IS BEING CREDITED, validated by exactly the same `DocumentLineSchema`
     * as an invoice's lines and computed by the same engine (§2.1 — the tax math
     * lives in one package and both surfaces import it).
     *
     * FREE-TYPED RATHER THAN A LINE PICKER, and the server makes that safe: rates
     * resolve as of the ORIGINAL INVOICE'S DATE, not today's, so crediting HSN
     * 7308 against a bill that charged 12% reverses 12% even if the Council has
     * since moved it to 18%. A picker copying invoice lines would produce the same
     * numbers; it is a client affordance, not a server contract, and building the
     * contract around it would have made a partial credit ("₹2,340 of the
     * ₹15,340") awkward to express.
     */
    lines: LinesSchema,
    notes: optionalText(5000),
});
export const CreditNoteCreateSchema = CreditNoteCreateBaseSchema.superRefine((doc, ctx) => {
    refineDocumentLines(doc.lines, ctx);
});
/**
 * Cancelling a credit note — a status, never a delete, and the number stays
 * consumed (§2, §17.3).
 *
 * IT PUTS THE MONEY BACK. Every sum over `credit_notes` counts ISSUED rows
 * only, so cancelling one returns its amount to what the customer owes — on the
 * invoice, the unpaid register, the statement and the home screen at once,
 * because all four read one producer. That is the correct meaning: a cancelled
 * credit note credits nothing.
 *
 * THE REASON IS MANDATORY for the reason it is on every other cancellation
 * here: the whole justification for cancelling rather than deleting is that the
 * record survives AND EXPLAINS ITSELF, and `audit_log` is an ADMIN surface the
 * owner cannot open.
 */
export const CreditNoteCancelSchema = z
    .object({
    reason: z
        .string()
        .trim()
        .min(1, "A reason is required to cancel a credit note")
        .max(500, "Reason is too long"),
})
    .strict();
export const CREDIT_NOTE_LIST_DEFAULT_LIMIT = 25;
export const CREDIT_NOTE_LIST_MAX_LIMIT = 100;
/**
 * The register's filters — register PARITY with the other three lists (§11.2),
 * plus the one question only a credit note raises: `invoiceId`.
 *
 * PARITY IS THE POINT. §17.7's challans shipped without `from`/`to` and without
 * amount bounds and the register was immediately less useful than the invoice
 * one beside it; the memory note "challans need parity when built" exists
 * because of it. So this list carries the same `q` / `sort` / `dir` / `status` /
 * `customerId` / date-range / amount-range / paging vocabulary, spelled the same
 * way, and the SAME refusals for a reversed range.
 */
export const CreditNoteListQuerySchema = z
    .object({
    /**
     * Free text over the note's OWN reference numbers — `credit_note_number` and
     * the frozen `original_invoice_number`, so "26-27/67" finds every note
     * raised against that bill.
     *
     * NOT the customer name: the buyer lives in `customer_snapshot`, a
     * text-backed JSON column, and CLAUDE.md forbids querying inside one. That
     * is what `customerId` is for.
     */
    q: optionalQuery(z.string().trim().max(64, "Search text is too long").optional()),
    sort: optionalQuery(z.enum(["number", "date", "customer", "status", "total"]).optional()),
    dir: optionalQuery(z.enum(["asc", "desc"]).optional()),
    status: optionalQuery(CreditNoteStatusSchema.optional()),
    /** Every note against one bill — what the invoice detail screen asks for. */
    invoiceId: optionalQuery(z.coerce
        .number()
        .int("invoiceId must be a whole number")
        .positive("invoiceId must be positive")
        .optional()),
    customerId: optionalQuery(z.coerce
        .number()
        .int("customerId must be a whole number")
        .positive("customerId must be positive")
        .optional()),
    from: optionalQuery(IsoDateSchema.optional()),
    to: optionalQuery(IsoDateSchema.optional()),
    /**
     * Inclusive bounds on the amount CREDITED, in integer paise.
     *
     * BOUNDS `grand_total_paise` DIRECTLY and correctly, for the structural
     * reason the challan register states: `tax_display_mode` exists only on
     * `model Quotation`, so a credit note has no second "printed" total that
     * could disagree with the stored one.
     *
     * BOTH BOUNDS ARE NON-NEGATIVE, like every other paise filter, and here that
     * is not merely convention: there is no negative amount anywhere in this
     * table to match (§17.3), so accepting one would be offering a filter for
     * rows that cannot exist.
     */
    minTotalPaise: optionalQuery(z.coerce.number().int("minTotalPaise must be a whole number of paise").min(0).optional()),
    maxTotalPaise: optionalQuery(z.coerce.number().int("maxTotalPaise must be a whole number of paise").min(0).optional()),
    limit: optionalQuery(z.coerce
        .number()
        .int("limit must be a whole number")
        .min(1, "limit must be at least 1")
        .max(CREDIT_NOTE_LIST_MAX_LIMIT, `limit cannot exceed ${CREDIT_NOTE_LIST_MAX_LIMIT}`)
        .default(CREDIT_NOTE_LIST_DEFAULT_LIMIT)),
    offset: optionalQuery(z.coerce
        .number()
        .int("offset must be a whole number")
        .min(0, "offset cannot be negative")
        .default(0)),
})
    .strict()
    .superRefine((query, ctx) => {
    if (query.from !== undefined && query.to !== undefined && query.to < query.from) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["to"],
            message: '"to" cannot be earlier than "from"',
        });
    }
    if (query.minTotalPaise !== undefined &&
        query.maxTotalPaise !== undefined &&
        query.minTotalPaise > query.maxTotalPaise) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["maxTotalPaise"],
            message: "maxTotalPaise cannot be smaller than minTotalPaise",
        });
    }
});
/* -------------------------------------------------------------------------- */
/* Completing a DRAFT invoice — PUT /invoices/:id (§7.1, §9.2)                  */
/* -------------------------------------------------------------------------- */
/**
 * "A reference date with no reference number", in ONE sentence per reference.
 *
 * §9.2's right-hand header block is four rows, each a value **plus** a date —
 * `Invoice No. | S.O. No. | Challan No. | Quotation No.` A date on its own
 * therefore prints an "S.O. Date" row above an empty "S.O. No." row, which is a
 * reference the customer's accounts payable cannot match to anything.
 *
 * The S.O. wording is deliberately identical to the one `QuotationConvertSchema`
 * already raises at conversion time: the same mistake, made at either end of the
 * same document's life, must not produce two different sentences.
 */
export const SO_DATE_NEEDS_NUMBER = "Enter the S.O. number as well as its date — the invoice prints them together";
export const CHALLAN_DATE_NEEDS_NUMBER = "Enter the challan number as well as its date — the invoice prints them together";
/**
 * `PUT /invoices/:id` — the ONLY request that ever reaches a stored invoice's own
 * columns, and the whole feature is what it refuses.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THIS IS NOT `invoice.edit`, AND MUST NEVER GROW INTO IT. §2.1 refuses to
 * create an `invoice.edit` capability for any role, because an issued GST
 * invoice is not amendable — the only corrections are cancelling it (the number
 * stays consumed, §4) or a credit note (§17.3). What this schema serves is the
 * DIFFERENT capability §2.1 does define, `invoice.edit_draft`, and the narrow
 * case §7.1 leaves behind: conversion without an S.O. number saves the bill as
 * a DRAFT, the customer's purchase order turns up weeks later, and until now
 * there was no way to put the number on a bill whose own number was already
 * spent. Completing what conversion left missing is all this is.
 *
 * WHAT IS ABSENT IS THE POINT — and `.strict()` is what makes absence mean
 * "400", not "silently dropped".
 *
 * Everywhere else in this file `z.object()`'s default key-stripping IS the
 * server-authority mechanism (see the header comment): a create that posts
 * `grandTotalPaise` simply has it deleted. That is right for a create, whose
 * every figure is recomputed from the lines a moment later, so a smuggled total
 * cannot survive. It is WRONG here. This route recomputes nothing — it writes
 * the columns it is given — so a stripped `lines` key would be a request to
 * change a numbered tax document that answered `200 OK` having changed nothing,
 * and the caller would have every reason to believe it worked. `.strict()`
 * turns each of those into the refusal it is. The precedent is
 * `InvoiceEInvoiceSchema`, strict for the same reason.
 *
 * Rejected by `.strict()`, each for its own reason:
 *  - `lines` — changing the lines of a numbered invoice IS editing a tax
 *    document, whatever the route is called. No line is read, written or
 *    recomputed here.
 *  - every `*Paise` total, `amountInWords` — the server is the only authority on
 *    money (§6), and these are stored results, not inputs.
 *  - `date` — it would move the bill into another financial year while it holds
 *    a number allocated in this one (§5), and every §17.4 return would then
 *    disagree with the series.
 *  - `invoiceNumber`, `status` — allocated once, inside the transaction that
 *    inserted the row (§6). Not a field.
 *  - `customerId` — the buyer is frozen in `customer_snapshot` on the day (§4).
 *  - `supplyType`, `placeOfSupply` — derived from the two state codes, never
 *    asserted by a client (§5).
 *  - `irn` / `ackNo` / `ackDate` / `qrCodeData` — `POST /:id/einvoice`'s, gated
 *    on `invoice.record_irn`, which the ACCOUNTANT holds and this does not.
 *  - `cancelledAt` / `cancelReason` — `POST /:id/cancel`'s, and cancelling
 *    requires a stated reason.
 *
 * WHAT IS HERE, and why each one earned its place. The test is narrow: is this a
 * reference that ORIGINATES OUTSIDE this business, on the customer's timetable,
 * and is therefore genuinely missing when conversion runs?
 *
 *  - `soNumber` / `soDate` — YES, and it is the case §7.1 names: "they arrive
 *    with the customer's purchase order weeks after the quotation and cannot be
 *    pre-filled. Do not block conversion if absent: save as DRAFT."
 *  - `challanNumber` / `challanDate` — YES. Same §9.2 row, same shape, and this
 *    column holds the CUSTOMER'S OWN delivery-note reference (their document,
 *    their numbering — unrelated to this business's own `CHL/` series). Nothing
 *    in conversion ever sets it, so it is missing on exactly the same terms as
 *    the S.O. number and arrives on the same timetable.
 *
 * And what was considered and EXCLUDED:
 *  - `quotationRef` / `quotationRefDate` — same §9.2 row, but on a converted
 *    invoice these are AUTO-LINKED by §7.1, not typed. Every row this route can
 *    reach already has them, correct and server-derived; accepting them would
 *    let a client make the printed reference disagree with `source_quote_id`.
 *  - `attnName` — a header field a human types, but not a late-arriving
 *    reference: conversion always fills it from the quotation or the buyer's
 *    default contact, so it is never missing. Accepting it would widen this from
 *    "complete what conversion left out" to "edit the header".
 *  - `copyType` — not data about the transaction. It is which statutory copy
 *    renders first, and the PDF route already takes it per render as `?copy=`.
 *  - `notes` / `terms` — free text on a tax document, always populated at
 *    conversion. The moment a PUT can rewrite the terms of a numbered invoice,
 *    "header fields only" has stopped meaning anything.
 *
 * OMITTED MEANS LEAVE ALONE; EXPLICIT `null` (or `""`) MEANS CLEAR. This is a
 * COMPLETION route, not the full replacement `PUT /quotations/:id` is — the
 * screen it serves fills in one field on a bill it did not otherwise touch, so
 * "the key is absent" cannot be allowed to wipe a stored value. `clearable()`
 * expresses exactly that distinction, and it is the trap documented on
 * `CustomerUpdateSchema.pan`, where `.partial()` alone had it backwards: the
 * request meaning "leave it alone" cleared the column and the request meaning
 * "clear it" was refused.
 *
 * The column caps are taken FROM `InvoiceCreateBaseSchema`'s own fields rather
 * than retyped, so `so_number @db.VarChar(64)` and this schema cannot drift.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export const InvoiceDraftUpdateBaseSchema = z.object({
    soNumber: clearableField(InvoiceCreateBaseSchema.shape.soNumber),
    soDate: clearableField(InvoiceCreateBaseSchema.shape.soDate),
    challanNumber: clearableField(InvoiceCreateBaseSchema.shape.challanNumber),
    challanDate: clearableField(InvoiceCreateBaseSchema.shape.challanDate),
});
/**
 * `.strict()` is applied to the BASE OBJECT, before the refinement — chaining it
 * after a `.superRefine()` does not typecheck, which is why
 * `QuotationConvertSchema` is written the same way round.
 *
 * The pairing rule here can only see the BODY, so it catches the unambiguous
 * contradiction: a date supplied in the same request that clears its number.
 * "A date supplied while the STORED number is null" needs the row in view and is
 * therefore checked in the route, against the merged result, in these same words.
 */
export const InvoiceDraftUpdateSchema = InvoiceDraftUpdateBaseSchema.strict().superRefine((body, ctx) => {
    if (Object.keys(body).length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Send at least one field to update",
        });
    }
    if (body.soDate !== undefined && body.soDate !== null && body.soNumber === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["soNumber"],
            message: SO_DATE_NEEDS_NUMBER,
        });
    }
    if (body.challanDate !== undefined && body.challanDate !== null && body.challanNumber === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["challanNumber"],
            message: CHALLAN_DATE_NEEDS_NUMBER,
        });
    }
});
/* -------------------------------------------------------------------------- */
/* Payments — §17.5, behind the `payments` flag                                */
/* -------------------------------------------------------------------------- */
/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT IS NOT IN THIS SECTION, AND WHY EACH ABSENCE IS DELIBERATE.
 *
 * NO `PaymentUpdateSchema`. §2.1 declines to create a `payment.edit` capability
 * at all: a payment is an amount, a date, a method and a reference, so
 * "editing" one is indistinguishable from replacing it, and two paths to the
 * same change give the audit trail two places to be incomplete. A row typed
 * wrong is deleted and re-entered, both writes audited.
 *
 * NO `outstandingPaise` ON ANY REQUEST. The server is the only authority on
 * money (§6). The client never computes a balance and sends it back — it sends
 * what was received, and the server subtracts. Because `z.object()` strips
 * unknown keys, a client that posts one simply has it dropped; the request
 * schema below is `.strict()` because silence there would be worse than a 400.
 *
 * NO REFUND, AND NO NEGATIVE AMOUNT. Refunds are not modelled (§17.5), and a
 * negative payment must never be invented to represent one: every outstanding
 * figure in the system is a `SUM` over this table, so one negative row would
 * make a customer look owed money he was never billed.
 * ═══════════════════════════════════════════════════════════════════════════
 */
/**
 * `POST /invoices/:id/payments` — record money received against one bill.
 *
 * `.strict()`, like `InvoiceDraftUpdateSchema` and for the same reason: nothing
 * here is recomputed from anything else, so a key this schema does not know
 * about is a request the caller believes was honoured. In particular
 * `invoiceId` is NOT a field — it comes from the URL, so a body naming a
 * different invoice cannot quietly pay the wrong one.
 */
export const PaymentCreateSchema = z
    .object({
    /**
     * The day the money arrived, as it appears on the bank statement — NOT the
     * day it was typed in (`created_at` records that separately). Entries are
     * routinely made days late from a passbook, and a statement that ordered
     * receipts by entry date would not reconcile against the bank.
     */
    paidOn: IsoDateSchema,
    /**
     * INTEGER PAISE, STRICTLY POSITIVE.
     *
     * `PaiseSchema` is non-negative, which is right for a stored total and
     * wrong here: a payment of zero is not a payment, it is an empty row that
     * makes the ledger longer without making it truer, and it would appear on a
     * customer statement as an event that never happened. The API enforces
     * `> 0` again inside the same transaction as the overpayment check, because
     * this schema guards the HTTP edge and nothing else.
     *
     * There is deliberately no CHECK constraint in the database: `01_schema.sql`
     * is GENERATED from prisma/schema.prisma, so a constraint written only into
     * the incremental migration would exist on the upgraded box and be missing
     * from every fresh install — the worst kind of difference.
     */
    amountPaise: PaiseSchema.refine((value) => value > 0, "A payment must be more than zero — nothing was received otherwise"),
    method: PaymentMethodSchema,
    /**
     * The UTR / cheque number / UPI reference / TDS certificate number — the
     * string that ties this row to a line on a bank statement, and the thing
     * that settles a dispute six months later ("he paid by NEFT on the 3rd,
     * ref HDFC0004567"). Optional because cash has none.
     *
     * Capped at 191 to match `reference @db.VarChar(191)`.
     */
    reference: optionalText(191),
    note: optionalText(500),
})
    .strict();
//# sourceMappingURL=documents.js.map